import {DatePipe, registerLocaleData} from '@angular/common';
import {HttpClient, HttpClientModule} from '@angular/common/http';
import localeEn from '@angular/common/locales/en';
import localeFr from '@angular/common/locales/fr';
import {APP_INITIALIZER, ErrorHandler, Injector, NgModule} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {Router} from '@angular/router';
import {JwtHelperService} from '@auth0/angular-jwt';
import {AppAsideModule, AppBreadcrumbModule, AppFooterModule, AppHeaderModule, AppSidebarModule} from '@coreui/angular';
import {IconModule, IconSetModule, IconSetService} from '@coreui/icons-angular';
import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {NgSelectModule} from '@ng-select/ng-select';
import {MissingTranslationHandler, TranslateLoader, TranslateModule, TranslateService} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import * as Sentry from '@sentry/angular';
import {AvatarModule} from 'ngx-avatars';
import {CarouselModule} from 'ngx-bootstrap/carousel';
// Import 3rd party components
import {BsDropdownModule} from 'ngx-bootstrap/dropdown';
import {BsModalService} from 'ngx-bootstrap/modal';
import {TabsModule} from 'ngx-bootstrap/tabs';
import {TimepickerActions} from 'ngx-bootstrap/timepicker';
import {NgxIntlTelInputModule} from 'ngx-intl-tel-input-gg';
import {LoggerModule, NGXLogger, NgxLoggerLevel} from 'ngx-logger';
import {PerfectScrollbarModule} from 'ngx-perfect-scrollbar';
import {environment} from '../assets/environments/environment';
import {commonProperties} from '../assets/environments/environment.common';
import {AccountValidationComponent} from './account-validation/account-validation.component';
import {AppComponent} from './app.component';
// Import routing module
import {AppRoutingModule} from './app.routing';
import {DefaultLayoutComponent} from './containers';
import {CoreModule} from './core/core.module';
import {AuthenticationService} from './core/services/authentication.service';
import {ForgetPasswordComponent} from './forget-password/forget-password.component';
import {LoginComponent} from './login/login.component';
import {ResetPasswordComponent} from './reset-password/reset-password.component';
import {
  PasswordMeterProgressBarComponent,
} from './shared/components/password-meter/password-meter-progress-bar/password-meter-progress-bar.component';
import {PasswordMeterComponent} from './shared/components/password-meter/password-meter.component';
import {SentryMissingTranslationHandler} from './shared/sentry-missing-translation-handler';
import {SharedModule} from './shared/shared.module';
import {SignupComponent} from './signup/signup.component';
import {EmployeeModule} from './views/employee/employee.module';
import {P404Component} from './views/error/404.component';
import {P500Component} from './views/error/500.component';
import {InvoiceModule} from './views/invoice/invoice.module';
import {MarketplaceModule} from './views/marketplace/marketplace.module';
import {MissionModule} from './views/mission/mission.module';
import {OnCallModule} from './views/on-call/on-call.module';
import {PurchaseOrderModule} from './views/purchase-order/purchase-order.module';

registerLocaleData(localeFr);
registerLocaleData(localeEn);

// required for AOT compilation
export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http);
}

export function initConfig(injector: Injector, logger: NGXLogger) {
  return () => {
    if (!environment.production) {
      const updatedConfig = logger.getConfigSnapshot();
      updatedConfig.level = NgxLoggerLevel.ERROR;
      logger.updateConfig(updatedConfig);
    }
    const jwtHelper = injector.get(JwtHelperService);
    const router = injector.get(Router);
    const token = localStorage.getItem(AuthenticationService.TOKEN);
    if (token) {
      if (jwtHelper.isTokenExpired()) {
        localStorage.removeItem('access_token');
        router.navigate(['login']);
      }

      const xhr = new XMLHttpRequest();
      xhr.open('POST', environment.api_root + commonProperties.tokenValidation);
      xhr.setRequestHeader('Content-Type', 'application/json;charset=UTF-8');

      xhr.addEventListener('readystatechange', () => {
        if (xhr.readyState === XMLHttpRequest.DONE && xhr.status !== 204) {
          localStorage.removeItem('access_token');
          router.navigate(['login']);
        }
      });

      xhr.send(JSON.stringify({
        token,
        type: 'SUPPLIER',
      }));
    }
  };
}

const APP_CONTAINERS = [DefaultLayoutComponent];

Sentry.init({
  dsn: 'https://e3444120e26e1fc1e66c7f9552c57130@o4509627437350912.ingest.de.sentry.io/4509627445215312',
  // Adds request headers and IP for users, for more info visit:
  // https://docs.sentry.io/platforms/javascript/guides/angular/configuration/options/#sendDefaultPii
  sendDefaultPii: true,
  integrations: [
    // Registers and configures the Tracing integration,
    // which automatically instruments your application to monitor its
    // performance, including custom Angular routing instrumentation
    Sentry.browserTracingIntegration(),
  ],
  environment: environment.env,
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for tracing.
  // We recommend adjusting this value in production
  // Learn more at
  // https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
  tracesSampleRate: 1.0,
  // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
  tracePropagationTargets: ['localhost', /^https:\/\/api\.qa\.isupplier\.fr/, /^https:\/\/api\.demo\.isupplier\.fr/, /^https:\/\/api\.staging\.isupplier\.fr/, /^https:\/\/api\.isupplier\.fr/],
});

@NgModule({
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        AppRoutingModule,
        AppAsideModule,
        AppBreadcrumbModule.forRoot(),
        AppFooterModule,
        AppHeaderModule,
        AppSidebarModule,
        PerfectScrollbarModule,
        BsDropdownModule.forRoot(),
        TabsModule.forRoot(),
        CarouselModule.forRoot(),
        CoreModule,
        IconModule,
        ReactiveFormsModule,
        IconSetModule.forRoot(),
        // ngx-translate and the loader module
        HttpClientModule,
        LoggerModule.forRoot({
            level: NgxLoggerLevel.DEBUG,
            timestampFormat: 'dd/MM/yyyy HH:mm:ss',
        }),
        NgxIntlTelInputModule,
        NgSelectModule,
        FormsModule,
        SharedModule,
        MissionModule,
        EmployeeModule,
        InvoiceModule,
        MarketplaceModule,
        OnCallModule,
        PurchaseOrderModule,
        AvatarModule,
        FontAwesomeModule,
        TranslateModule.forRoot({
            missingTranslationHandler: {provide: MissingTranslationHandler, useClass: SentryMissingTranslationHandler},
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient],
            },
        })
    ],
  declarations: [
    AppComponent,
    ...APP_CONTAINERS,
    P404Component,
    P500Component,
    SignupComponent,
    LoginComponent,
    ForgetPasswordComponent,
    ResetPasswordComponent,
    AccountValidationComponent,
    PasswordMeterProgressBarComponent,
    PasswordMeterComponent,
  ],
  providers: [
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    DatePipe,
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: initConfig,
      deps: [Injector, NGXLogger, Sentry.TraceService],
    },
    IconSetService,
    TimepickerActions,
    TranslateService,
    BsModalService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
